import { FunctionComponent, JSX, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'

import { getUrlParam, getUrlParams } from 'core-components/helpers/url'
import { getHomePage } from 'core-components/sitebuilder/layout/helpers/sitePages'
import { ILayoutSelectorProps, LayoutSelector } from 'core-components/sitebuilder/layout/Selector'
import { selectRoles } from 'core-components/sitebuilder/store/selectors/selectRoles'
import { selectRouteParams } from 'core-components/sitebuilder/store/selectors/selectRouteParams'
import { selectSitePages } from 'core-components/sitebuilder/store/selectors/selectSitePages'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { setRouteParams } from 'core-components/sitebuilder/store/siteBuilderSlice'

interface IPrivateRoute {
  tracker?: (
    WrappedComponent: FunctionComponent<ILayoutSelectorProps>
  ) => (props: ILayoutSelectorProps) => JSX.Element
  EmptyLayout: FunctionComponent
}

export const PrivateRoute = ({ tracker, EmptyLayout }: IPrivateRoute) => {
  const user = useSelector(selectUser)
  const roles = useSelector(selectRoles)
  const location = useLocation()
  const sitePages = useSelector(selectSitePages)
  const dispatch = useDispatch()
  const currentRouteParams = useSelector(selectRouteParams)

  const homePage = getHomePage(sitePages, user, roles)
  const nextPageId: string = getUrlParam('page')
  const nextPage = useMemo(() => {
    return sitePages.find((el) => el.id === nextPageId)
  }, [nextPageId])

  const Component = useMemo(() => {
    return tracker ? tracker(LayoutSelector) : LayoutSelector
  }, [tracker])

  const needRedirect = useMemo(() => {
    if (!nextPage || currentRouteParams?.page === nextPage.id) {
      return false
    }

    return (
      nextPage.redirectUrl &&
      !location.pathname.includes(nextPage.redirectUrl) &&
      nextPage.redirectWindow === 'This tab' &&
      nextPage.redirectUrl.startsWith('/page/')
    )
  }, [nextPage, currentRouteParams, location])

  useEffect(() => {
    const routeParams = getUrlParams()
    if (JSON.stringify(currentRouteParams) !== JSON.stringify(routeParams)) {
      dispatch(setRouteParams(routeParams))
    }
  }, [currentRouteParams, dispatch, location])

  if (!homePage) {
    return <Navigate to={'/404'} />
  }
  if (location.pathname === '/') {
    return <Navigate to={'/page/' + homePage.id} />
  }
  if (needRedirect) {
    return <Navigate to={nextPage!.redirectUrl} replace />
  }

  return <Component pageId={nextPageId} EmptyLayout={EmptyLayout} />
}
